import { useState, useCallback, useEffect } from 'react';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle, XCircle, Download, Loader2, FileText, ArrowLeftRight, Settings, Mail, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { EmailTagInput } from '../components/ui/email-tag-input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0';
  return `$${Math.round(amount).toLocaleString('es-CL')}`;
};

// Create axios instance for RCV operations to avoid fetch body stream issues
const rcvApi = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 120000,
  withCredentials: true,
});

export default function RCVComparator() {
  const [rcvType, setRcvType] = useState('compra');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('faltantes');
  
  // Config state
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    schedule_hour: 7,
    schedule_minute: 30,
    notification_emails: [],
    rcv_compra_filename: null,
    rcv_venta_filename: null
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [configCompraFile, setConfigCompraFile] = useState(null);
  const [configVentaFile, setConfigVentaFile] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await rcvApi.get('/rcv/config');
      if (response.data) {
        setConfig({
          ...response.data,
          notification_emails: response.data.notification_emails || []
        });
      }
    } catch (err) { console.error('caught:', err); }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
      // Auto-detect type
      const filename = selectedFile.name.toUpperCase();
      if (filename.includes('VENTA')) {
        setRcvType('venta');
      } else {
        setRcvType('compra');
      }
    } else {
      setError('Por favor seleccione un archivo CSV válido');
      setFile(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      setError(null);
      setResult(null);
      const filename = droppedFile.name.toUpperCase();
      if (filename.includes('VENTA')) {
        setRcvType('venta');
      } else {
        setRcvType('compra');
      }
    } else {
      setError('Por favor seleccione un archivo CSV válido');
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const compareRCV = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await rcvApi.post(`/rcv/compare?rcv_type=${rcvType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult(response.data);
      
      if (response.data.resumen.total_faltantes_alegra > 0) {
        toast.warning(`Se encontraron ${response.data.resumen.total_faltantes_alegra} facturas faltantes en Alegra`);
      } else {
        toast.success('Todas las facturas del RCV están en Alegra');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error al procesar archivo';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!file) return;
    
    setDownloadingExcel(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await rcvApi.post(`/rcv/compare-excel?rcv_type=${rcvType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comparacion_rcv_${rcvType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Reporte Excel descargado');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error al generar Excel';
      toast.error(errorMessage);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const saveConfig = async () => {
    if (config.notification_emails.length === 0) {
      toast.error('Agregue al menos un email de notificación');
      return;
    }
    
    setSavingConfig(true);
    try {
      await rcvApi.post('/rcv/config', config);
      toast.success('Configuración guardada');
      loadConfig();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error guardando configuración';
      toast.error(errorMessage);
    } finally {
      setSavingConfig(false);
    }
  };

  const uploadConfigFiles = async () => {
    if (!configCompraFile && !configVentaFile) {
      toast.error('Seleccione al menos un archivo RCV');
      return;
    }
    
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      
      if (configCompraFile) formData.append('rcv_compra', configCompraFile);
      if (configVentaFile) formData.append('rcv_venta', configVentaFile);
      
      await rcvApi.post('/rcv/upload-files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Archivos subidos correctamente');
      setConfigCompraFile(null);
      setConfigVentaFile(null);
      loadConfig();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error subiendo archivos';
      toast.error(errorMessage);
    } finally {
      setUploadingFiles(false);
    }
  };

  const sendReportNow = async () => {
    if (!config.rcv_compra_filename && !config.rcv_venta_filename) {
      toast.error('Primero suba archivos RCV en la configuración');
      return;
    }
    
    if (config.notification_emails.length === 0) {
      toast.error('Configure al menos un email de notificación');
      return;
    }
    
    setSendingNow(true);
    try {
      const response = await rcvApi.post('/rcv/send-report-now');
      toast.success(response.data?.message || 'Reporte enviado correctamente');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error enviando reporte';
      toast.error(errorMessage);
    } finally {
      setSendingNow(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="rcv-comparator-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ArrowLeftRight className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Comparador RCV vs Alegra</h1>
            <p className="text-gray-500">Compare registros del SII con facturas de compra/venta en Alegra</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowConfig(!showConfig)}
          data-testid="toggle-config-btn"
        >
          <Settings className="h-4 w-4 mr-2" />
          {showConfig ? 'Ocultar' : 'Configurar'} Envío Diario
        </Button>
      </div>

      {/* Daily Report Configuration */}
      {showConfig && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Mail className="h-5 w-5" />
              Configuración de Envío Diario Automático
            </CardTitle>
            <CardDescription>
              Configure el envío automático diario de la comparación RCV por email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
                data-testid="enable-daily-switch"
              />
              <Label>Habilitar envío diario automático</Label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Hora de envío</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={config.schedule_hour}
                    onChange={(e) => setConfig({ ...config, schedule_hour: parseInt(e.target.value) || 0 })}
                    className="w-20"
                  />
                  <span className="self-center">:</span>
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    value={config.schedule_minute}
                    onChange={(e) => setConfig({ ...config, schedule_minute: parseInt(e.target.value) || 0 })}
                    className="w-20"
                  />
                </div>
              </div>
              
              <div>
                <Label>Email(s) de notificación</Label>
                <div className="mt-1">
                  <EmailTagInput
                    emails={config.notification_emails}
                    onChange={(emails) => setConfig({ ...config, notification_emails: emails })}
                    placeholder="Agregar email de notificación..."
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-medium">Archivos RCV para comparación diaria</Label>
              <p className="text-sm text-gray-500 mb-3">Suba los archivos RCV que se usarán en la comparación automática</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">RCV Compras</Label>
                  {config.rcv_compra_filename && (
                    <p className="text-xs text-green-600">Actual: {config.rcv_compra_filename}</p>
                  )}
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setConfigCompraFile(e.target.files[0])}
                    className="text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">RCV Ventas</Label>
                  {config.rcv_venta_filename && (
                    <p className="text-xs text-green-600">Actual: {config.rcv_venta_filename}</p>
                  )}
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setConfigVentaFile(e.target.files[0])}
                    className="text-sm"
                  />
                </div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={uploadConfigFiles}
                disabled={uploadingFiles || (!configCompraFile && !configVentaFile)}
                className="mt-3"
              >
                {uploadingFiles ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Subir Archivos
              </Button>
            </div>
            
            <div className="flex gap-3 pt-4 border-t">
              <Button onClick={saveConfig} disabled={savingConfig}>
                {savingConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Guardar Configuración
              </Button>
              
              <Button 
                variant="outline" 
                onClick={sendReportNow} 
                disabled={sendingNow || (!config.rcv_compra_filename && !config.rcv_venta_filename) || config.notification_emails.length === 0}
              >
                {sendingNow ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                {sendingNow ? 'Enviando...' : 'Enviar Ahora'}
              </Button>
            </div>
            
            {config.last_sent && (
              <p className="text-xs text-gray-500">
                Último envío: {new Date(config.last_sent).toLocaleString('es-CL')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Type Selector */}
      <div className="flex gap-2">
        <Button
          variant={rcvType === 'compra' ? 'default' : 'outline'}
          onClick={() => setRcvType('compra')}
          size="sm"
        >
          RCV Compras
        </Button>
        <Button
          variant={rcvType === 'venta' ? 'default' : 'outline'}
          onClick={() => setRcvType('venta')}
          size="sm"
        >
          RCV Ventas
        </Button>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Cargar Archivo RCV {rcvType === 'compra' ? 'Compras' : 'Ventas'}
          </CardTitle>
          <CardDescription>
            Suba el archivo CSV del Registro de {rcvType === 'compra' ? 'Compras' : 'Ventas'} (RCV) descargado del SII
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              file ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
            }`}
          >
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileText className="h-12 w-12 text-green-600" />
                <p className="font-medium text-green-700">{file.name}</p>
                <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { setFile(null); setResult(null); }}
                >
                  Cambiar archivo
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-12 w-12 text-gray-400" />
                <p className="text-gray-600">Arrastre un archivo CSV aquí o</p>
                <label className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">seleccione un archivo</span>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="rcv-file-input"
                  />
                </label>
                <p className="text-xs text-gray-400 mt-2">Formatos soportados: RCV_COMPRA_*.csv</p>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {file && (
            <div className="flex gap-3 mt-4 justify-center">
              <Button 
                onClick={compareRCV} 
                disabled={loading}
                data-testid="compare-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Comparando...
                  </>
                ) : (
                  <>
                    <ArrowLeftRight className="h-4 w-4 mr-2" />
                    Comparar con Alegra
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline"
                onClick={downloadExcel} 
                disabled={downloadingExcel || loading}
                data-testid="download-excel-btn"
              >
                {downloadingExcel ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Descargar Excel
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Section */}
      {result && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total RCV</p>
                    <p className="text-2xl font-bold">{result.resumen.total_rcv}</p>
                    <p className="text-xs text-gray-400">{formatCurrency(result.resumen.monto_total_rcv)}</p>
                  </div>
                  <FileText className="h-10 w-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Coincidencias</p>
                    <p className="text-2xl font-bold text-green-600">{result.resumen.total_coincidencias}</p>
                  </div>
                  <CheckCircle className="h-10 w-10 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className={result.resumen.total_faltantes_alegra > 0 ? 'border-red-200 bg-red-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Faltantes en Alegra</p>
                    <p className={`text-2xl font-bold ${result.resumen.total_faltantes_alegra > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                      {result.resumen.total_faltantes_alegra}
                    </p>
                    <p className="text-xs text-red-500">{formatCurrency(result.resumen.monto_faltante)}</p>
                  </div>
                  <XCircle className="h-10 w-10 text-red-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className={result.resumen.total_diferencias_monto > 0 ? 'border-yellow-200 bg-yellow-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Diferencias Monto</p>
                    <p className={`text-2xl font-bold ${result.resumen.total_diferencias_monto > 0 ? 'text-yellow-600' : 'text-gray-600'}`}>
                      {result.resumen.total_diferencias_monto}
                    </p>
                    <p className="text-xs text-yellow-600">{formatCurrency(result.resumen.monto_diferencias)}</p>
                  </div>
                  <AlertTriangle className="h-10 w-10 text-yellow-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Period Info */}
          <div className="text-sm text-gray-500 text-center">
            Período analizado: <span className="font-medium">{result.periodo.desde}</span> a <span className="font-medium">{result.periodo.hasta}</span>
          </div>

          {/* Tabs */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex gap-2">
                <Button
                  variant={activeTab === 'faltantes' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab('faltantes')}
                  className={activeTab === 'faltantes' ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Faltantes ({result.resumen.total_faltantes_alegra})
                </Button>
                <Button
                  variant={activeTab === 'diferencias' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab('diferencias')}
                  className={activeTab === 'diferencias' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
                >
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Diferencias ({result.resumen.total_diferencias_monto})
                </Button>
                <Button
                  variant={activeTab === 'coincidencias' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab('coincidencias')}
                  className={activeTab === 'coincidencias' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Coincidencias ({result.resumen.total_coincidencias})
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Faltantes Tab */}
              {activeTab === 'faltantes' && (
                <div className="overflow-x-auto">
                  {result.faltantes_en_alegra.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                      <p>No hay facturas faltantes en Alegra</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm" data-testid="faltantes-table">
                      <thead>
                        <tr className="bg-red-50 text-red-800">
                          <th className="px-3 py-2 text-left">Folio</th>
                          <th className="px-3 py-2 text-left">RUT {result.tipo === 'venta' ? 'Cliente' : 'Proveedor'}</th>
                          <th className="px-3 py-2 text-left">Razón Social</th>
                          <th className="px-3 py-2 text-left">Fecha</th>
                          <th className="px-3 py-2 text-left">Tipo</th>
                          <th className="px-3 py-2 text-right">Neto</th>
                          <th className="px-3 py-2 text-right">IVA</th>
                          <th className="px-3 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.faltantes_en_alegra.map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-red-50">
                            <td className="px-3 py-2 font-mono">{item.rcv.folio}</td>
                            <td className="px-3 py-2 font-mono">{item.rcv.rut_contraparte}</td>
                            <td className="px-3 py-2">{item.rcv.razon_social}</td>
                            <td className="px-3 py-2">{item.rcv.fecha_docto}</td>
                            <td className="px-3 py-2">{item.rcv.tipo_doc_nombre}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.rcv.monto_neto)}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.rcv.monto_iva)}</td>
                            <td className="px-3 py-2 text-right font-semibold">{formatCurrency(item.rcv.monto_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Diferencias Tab */}
              {activeTab === 'diferencias' && (
                <div className="overflow-x-auto">
                  {result.diferencias_monto.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                      <p>No hay diferencias de monto</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm" data-testid="diferencias-table">
                      <thead>
                        <tr className="bg-yellow-50 text-yellow-800">
                          <th className="px-3 py-2 text-left">Folio</th>
                          <th className="px-3 py-2 text-left">Proveedor</th>
                          <th className="px-3 py-2 text-left">Fecha</th>
                          <th className="px-3 py-2 text-right">Monto RCV</th>
                          <th className="px-3 py-2 text-right">Monto Alegra</th>
                          <th className="px-3 py-2 text-right">Diferencia</th>
                          <th className="px-3 py-2 text-left">Nota</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.diferencias_monto.map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-yellow-50">
                            <td className="px-3 py-2 font-mono">{item.rcv.folio}</td>
                            <td className="px-3 py-2">{item.rcv.razon_social}</td>
                            <td className="px-3 py-2">{item.rcv.fecha_docto}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.rcv.monto_total)}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.alegra?.total)}</td>
                            <td className={`px-3 py-2 text-right font-semibold ${item.diferencia > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {formatCurrency(item.diferencia)}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500">{item.nota || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Coincidencias Tab */}
              {activeTab === 'coincidencias' && (
                <div className="overflow-x-auto">
                  {result.coincidencias.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-2" />
                      <p>No se encontraron coincidencias</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm" data-testid="coincidencias-table">
                      <thead>
                        <tr className="bg-green-50 text-green-800">
                          <th className="px-3 py-2 text-left">Folio</th>
                          <th className="px-3 py-2 text-left">RUT</th>
                          <th className="px-3 py-2 text-left">{result.tipo === 'venta' ? 'Cliente' : 'Proveedor'} RCV</th>
                          <th className="px-3 py-2 text-left">{result.tipo === 'venta' ? 'Cliente' : 'Proveedor'} Alegra</th>
                          <th className="px-3 py-2 text-right">Monto</th>
                          <th className="px-3 py-2 text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.coincidencias.slice(0, 100).map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-green-50">
                            <td className="px-3 py-2 font-mono">{item.rcv.folio}</td>
                            <td className="px-3 py-2 font-mono">{item.rcv.rut_contraparte}</td>
                            <td className="px-3 py-2">{item.rcv.razon_social}</td>
                            <td className="px-3 py-2">{item.alegra?.proveedor_nombre || item.alegra?.cliente_nombre}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.rcv.monto_total)}</td>
                            <td className="px-3 py-2 text-center">
                              {item.status === 'match' ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" /> OK
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800" title={item.nota}>
                                  <AlertTriangle className="h-3 w-3 mr-1" /> Parcial
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {result.coincidencias.length > 100 && (
                    <p className="text-center text-sm text-gray-500 mt-2">
                      Mostrando 100 de {result.coincidencias.length} coincidencias. Descargue el Excel para ver todas.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Download Excel Button */}
          <div className="flex justify-center">
            <Button onClick={downloadExcel} disabled={downloadingExcel} size="lg">
              {downloadingExcel ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Generando Excel...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  Descargar Reporte Completo en Excel
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
